import React, { useState } from 'react';
import { FiX, FiSend, FiCheckCircle, FiBriefcase, FiCalendar, FiMapPin, FiUser, FiMail, FiPhone, FiMessageSquare } from 'react-icons/fi';
import api from '../services/api';

export type InteresseTipo = 'parceiro' | 'promotor' | 'agente';

interface InteresseModalProps {
  tipo: InteresseTipo;
  onClose: () => void;
}

const CONFIG = {
  parceiro: {
    icon: <FiBriefcase className="text-2xl" />,
    title: 'Ser Parceiro Pagali',
    subtitle: 'Junte-se à nossa rede de parceiros e faça crescer o seu negócio.',
    gradient: 'from-amber-500 to-orange-500',
    accent: 'text-amber-600',
    border: 'border-amber-200',
    bg: 'bg-amber-50',
    btnClass: 'bg-amber-500 hover:bg-amber-600',
    empresaLabel: 'Empresa / Organização',
    mensagemPlaceholder: 'Conte-nos sobre o seu negócio e como gostaria de colaborar com o Pagali…',
  },
  promotor: {
    icon: <FiCalendar className="text-2xl" />,
    title: 'Promotor de Eventos',
    subtitle: 'Vende bilhetes e gere pagamentos cashless nos seus eventos com o Pagali.',
    gradient: 'from-violet-500 to-purple-600',
    accent: 'text-violet-600',
    border: 'border-violet-200',
    bg: 'bg-violet-50',
    btnClass: 'bg-violet-600 hover:bg-violet-700',
    empresaLabel: 'Nome do Evento / Organização',
    mensagemPlaceholder: 'Descreva o tipo de eventos que organiza, frequência, número esperado de participantes…',
  },
  agente: {
    icon: <FiMapPin className="text-2xl" />,
    title: 'Seja um Agente Pagali',
    subtitle: 'Faça recargas e pagamentos de serviços aos seus clientes e ganhe comissão em cada operação.',
    gradient: 'from-sky-500 to-cyan-600',
    accent: 'text-sky-600',
    border: 'border-sky-200',
    bg: 'bg-sky-50',
    btnClass: 'bg-sky-600 hover:bg-sky-700',
    empresaLabel: 'Nome do Estabelecimento / Localidade',
    mensagemPlaceholder: 'Diga-nos onde fica o seu estabelecimento e que serviços gostaria de oferecer aos seus clientes…',
  },
};

const InteresseModal: React.FC<InteresseModalProps> = ({ tipo, onClose }) => {
  const cfg = CONFIG[tipo];

  const [form, setForm] = useState({ nome: '', email: '', telefone: '', empresa: '', mensagem: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.email.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await api.post('/public/interesse', { tipo, ...form });
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao enviar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className={`bg-gradient-to-r ${cfg.gradient} p-6 relative`}>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
          >
            <FiX className="text-xl" />
          </button>
          <div className="flex items-center gap-3 text-white">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              {cfg.icon}
            </div>
            <div>
              <h2 className="text-lg font-bold">{cfg.title}</h2>
              <p className="text-sm text-white/80">{cfg.subtitle}</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {success ? (
            <div className="text-center py-4">
              <FiCheckCircle className="text-5xl text-green-500 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-pagali-gray-800 mb-1">Pedido enviado!</h3>
              <p className="text-sm text-pagali-gray-500 mb-6">
                A equipa Pagali vai analisar o seu interesse e entrar em contacto em breve.
              </p>
              <button
                onClick={onClose}
                className="btn-primary"
              >
                Fechar
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Nome */}
              <div>
                <label className="block text-xs font-semibold text-pagali-gray-700 mb-1">
                  Nome completo <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <FiUser className="absolute left-3 top-1/2 -translate-y-1/2 text-pagali-gray-400 text-sm" />
                  <input
                    type="text"
                    required
                    value={form.nome}
                    onChange={set('nome')}
                    placeholder="O seu nome"
                    className="w-full pl-9 pr-3 py-2.5 text-sm border border-pagali-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-transparent transition"
                    style={{ '--tw-ring-color': tipo === 'parceiro' ? '#f59e0b' : '#8b5cf6' } as any}
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-semibold text-pagali-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-pagali-gray-400 text-sm" />
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={set('email')}
                    placeholder="email@exemplo.com"
                    className="w-full pl-9 pr-3 py-2.5 text-sm border border-pagali-gray-200 rounded-xl focus:outline-none focus:ring-2 transition"
                  />
                </div>
              </div>

              {/* Telefone + Empresa em linha */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-pagali-gray-700 mb-1">Telefone</label>
                  <div className="relative">
                    <FiPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-pagali-gray-400 text-sm" />
                    <input
                      type="tel"
                      value={form.telefone}
                      onChange={set('telefone')}
                      placeholder="+238 9XX XXXX"
                      className="w-full pl-9 pr-3 py-2.5 text-sm border border-pagali-gray-200 rounded-xl focus:outline-none focus:ring-2 transition"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-pagali-gray-700 mb-1">{cfg.empresaLabel}</label>
                  <div className="relative">
                    <FiBriefcase className="absolute left-3 top-1/2 -translate-y-1/2 text-pagali-gray-400 text-sm" />
                    <input
                      type="text"
                      value={form.empresa}
                      onChange={set('empresa')}
                      placeholder="Nome"
                      className="w-full pl-9 pr-3 py-2.5 text-sm border border-pagali-gray-200 rounded-xl focus:outline-none focus:ring-2 transition"
                    />
                  </div>
                </div>
              </div>

              {/* Mensagem */}
              <div>
                <label className="block text-xs font-semibold text-pagali-gray-700 mb-1">
                  <FiMessageSquare className="inline mr-1" />Mensagem
                </label>
                <textarea
                  rows={3}
                  value={form.mensagem}
                  onChange={set('mensagem')}
                  placeholder={cfg.mensagemPlaceholder}
                  className="w-full px-3 py-2.5 text-sm border border-pagali-gray-200 rounded-xl focus:outline-none focus:ring-2 transition resize-none"
                />
              </div>

              {error && (
                <p className="text-xs text-red-500 bg-red-50 rounded-lg px-3 py-2">{error}</p>
              )}

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 py-2.5 text-sm font-medium text-pagali-gray-600 border border-pagali-gray-200 rounded-xl hover:bg-pagali-gray-50 transition"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading || !form.nome || !form.email}
                  className={`flex-1 py-2.5 text-sm font-semibold text-white rounded-xl transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${cfg.btnClass}`}
                >
                  {loading ? (
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <FiSend className="text-sm" />
                      Enviar pedido
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default InteresseModal;
